import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Input } from '../input';
import { Button } from '../button';
import { useUser } from '../UserContext';
import { useOrgMiembros } from '../../hooks/useOrgMiembros';
import { colorDe, inicialesDe } from '../AsignadoBubble';
import { MENSAJE_MAX_LEN, OT_RESULTADO_LABELS, partirMenciones } from '../../utils/ordenesTrabajo';

const formatFechaHora = (ms) => {
  if (!ms) return '';
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// One line per system event. The thread doubles as the historial, so these
// read as what happened, not as data.
function textoEvento(m) {
  const d = m.datos || {};
  switch (m.evento) {
    case 'creada':
      return d.faseNombre ? `abrió la orden en la fase ${d.faseNombre}` : 'abrió la orden';
    case 'fase': {
      const base = d.deNombre ? `avanzó la orden de ${d.deNombre} a ${d.aNombre || d.a}` : `avanzó la orden a ${d.aNombre || d.a}`;
      const quien = d.nombres ? ` y la asignó a ${d.nombres}` : (Array.isArray(d.asignados) && d.asignados.length === 0 ? ' sin asignados' : '');
      return `${base}${quien}${d.nota ? `. ${d.nota}` : ''}`;
    }
    case 'aprobacion': {
      if (d.resultado === 'aprobada') {
        return `aprobó ${d.faseNombre ? `la fase ${d.faseNombre}` : 'la orden'}${d.aNombre ? ` y la avanzó a ${d.aNombre}` : ''}${d.nombres ? `, asignada a ${d.nombres}` : ''}${d.motivo ? `. ${d.motivo}` : ''}`;
      }
      return `rechazó ${d.faseNombre ? `la fase ${d.faseNombre}` : 'la orden'}${d.aNombre ? ` y la regresó a ${d.aNombre}` : ''}${d.nombres ? ` con ${d.nombres}` : ''}${d.motivo ? `. ${d.motivo}` : ''}`;
    }
    case 'faseTerminada':
      return `terminó ${d.faseNombre ? `la fase ${d.faseNombre}` : 'la fase'}${d.nota ? `. ${d.nota}` : ''}`;
    case 'asignacion':
      return d.nombres ? `asignó a ${d.nombres}` : 'quitó a los asignados';
    case 'equipos':
      return d.nombres ? `puso los equipos: ${d.nombres}` : 'quitó los equipos';
    case 'cierre':
      return `cerró la orden como ${(OT_RESULTADO_LABELS[d.resultado] || d.resultado || '').toLowerCase()}${d.motivo ? `. ${d.motivo}` : ''}`;
    case 'reapertura':
      return `reabrió la orden${d.motivo ? `. ${d.motivo}` : ''}`;
    case 'tarea':
      // Legacy events from the removed tareas list (2026-10-19).
      if (d.nueva) return `agregó trabajo: ${d.titulo}`;
      return d.hecha ? `terminó: ${d.titulo}` : `reabrió: ${d.titulo}`;
    case 'relacion':
      return 'cambió la relación de la orden';
    default:
      return m.evento || 'actualizó la orden';
  }
}

/**
 * The OT's thread: people's messages and system events in one stream, oldest
 * first, with a composer. Same visual language as PendienteNotas.
 *
 * @param {Object} props
 * @param {Object} props.ot
 * @param {Array<Object>} props.mensajes
 * @param {boolean} props.loading
 * @param {Function} props.onEnviar  - (texto)
 * @param {boolean} [props.cerrada]
 */
export function OtHilo({ ot, mensajes = [], loading = false, onEnviar, cerrada = false }) {
  const { user, userData } = useUser();
  const { miembros, porId, nombreDe } = useOrgMiembros();
  const [texto, setTexto] = useState('');
  const [busy, setBusy] = useState(false);
  const [sugerida, setSugerida] = useState(0);
  const finRef = useRef(null);

  // @ at the end of what is being typed (optionally followed by a quote and
  // the start of a name) opens the member picker; picking writes @"Nombre".
  const tag = /@"?([^"@]*)$/.exec(texto);
  const sugerencias = tag
    ? miembros
      .filter((m) => m.nombre && m.id !== user?.uid)
      .filter((m) => m.nombre.toLowerCase().includes(tag[1].trim().toLowerCase()))
      .slice(0, 6)
    : [];
  const elegir = (m) => {
    setTexto(texto.slice(0, tag.index) + `@"${m.nombre}" `);
    setSugerida(0);
  };

  useEffect(() => {
    finRef.current?.scrollIntoView({ block: 'nearest' });
  }, [mensajes.length]);

  const quien = (m) => {
    if (m.autor === user?.uid) return userData?.nombre || userData?.correo || 'Yo';
    return nombreDe(m.autor) || m.autorNombre || 'Alguien';
  };
  const miembroDe = (m) => porId[m.autor] || { nombre: m.autorNombre, correo: '' };

  const enviar = async () => {
    const limpio = texto.trim();
    if (!limpio || busy) return;
    setBusy(true);
    try {
      await onEnviar(limpio);
      setTexto('');
    } catch (e) {
      console.error('enviar mensaje OT failed', e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        {loading && mensajes.length === 0 ? (
          <div className="text-xs text-zinc-400 py-2">Cargando…</div>
        ) : mensajes.length === 0 ? (
          <div className="text-xs text-zinc-500 py-2">Sin mensajes.</div>
        ) : (
          <ol className="relative border-l border-gray-200 ml-3 space-y-3 py-1">
            {mensajes.map((m) => {
              const esEvento = m.tipo === 'evento';
              return (
                <li key={m.id} className="ml-5">
                  <span
                    className={clsx(
                      'absolute -left-3 flex items-center justify-center rounded-full border-2 border-white text-[10px] font-semibold w-6 h-6 -mt-0.5',
                      esEvento ? 'bg-zinc-200 text-zinc-600' : colorDe(m.autor),
                    )}
                    title={quien(m)}
                  >
                    {esEvento ? '·' : inicialesDe(miembroDe(m))}
                  </span>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className={clsx('text-sm', esEvento ? 'text-zinc-500' : 'font-semibold text-gray-800')}>
                      {quien(m)}{esEvento ? ` ${textoEvento(m)}` : ''}
                    </span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{formatFechaHora(m.at)}</span>
                  </div>
                  {!esEvento && (
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">
                      {partirMenciones(m.texto).map((p, i) => (p.tipo === 'mencion'
                        ? <span key={i} className="rounded bg-azul-surelia/10 px-1 font-medium text-azul-surelia">@{p.valor}</span>
                        : <React.Fragment key={i}>{p.valor}</React.Fragment>))}
                    </div>
                  )}
                </li>
              );
            })}
            <li ref={finRef} />
          </ol>
        )}
      </div>
      <div className="flex items-center gap-2 pt-3 border-t border-zinc-100 mt-2">
        <div className="flex-1 min-w-0 relative">
          {sugerencias.length > 0 && (
            <ul className="absolute bottom-full left-0 mb-1 w-64 rounded-lg border border-zinc-200 bg-white shadow-lg py-1 z-10">
              {sugerencias.map((m, i) => (
                <li key={m.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); elegir(m); }}
                    className={clsx('w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm', i === sugerida ? 'bg-azul-surelia/10 text-azul-surelia' : 'text-zinc-700 hover:bg-zinc-50')}
                  >
                    <span className={clsx('flex items-center justify-center rounded-full w-5 h-5 text-[10px] font-semibold', colorDe(m.id))}>{inicialesDe(m)}</span>
                    <span className="truncate">{m.nombre}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Input
            type="text"
            value={texto}
            maxLength={MENSAJE_MAX_LEN}
            disabled={busy || cerrada}
            onChange={(e) => { setTexto(e.target.value); setSugerida(0); }}
            onKeyDown={(e) => {
              if (sugerencias.length) {
                if (e.key === 'ArrowDown') { e.preventDefault(); setSugerida((i) => (i + 1) % sugerencias.length); return; }
                if (e.key === 'ArrowUp') { e.preventDefault(); setSugerida((i) => (i - 1 + sugerencias.length) % sugerencias.length); return; }
                if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); elegir(sugerencias[sugerida] || sugerencias[0]); return; }
                if (e.key === 'Escape') { setTexto(texto.slice(0, tag.index)); return; }
              }
              if (e.key === 'Enter' && !e.shiftKey) enviar();
            }}
            placeholder={cerrada ? 'La orden está cerrada' : 'Escribe al equipo, @ para etiquetar'}
          />
        </div>
        <Button color="surelia" disabled={busy || cerrada || !texto.trim()} onClick={enviar}>
          {busy ? 'Enviando…' : 'Enviar'}
        </Button>
      </div>
    </div>
  );
}

export default OtHilo;
